import { DeclarationDocumentViewer } from "@/components/common/PdfViewer/DeclarationDocumentViewer"
import { toast } from "@/components/common/Toast"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { PERMISSIONS } from "@/constants/permissions"
import {
  useFeatureFlag,
  useGetAllFeatureFlags,
} from "@/contexts/FeatureFlagContext"
import { SETTINGS_PATHS } from "@/features/settings/paths"
import { useInquirySettingsAccess } from "@/features/settings/useSettingsVisibility"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { cn } from "@/lib/utils"
import { useOrgSignatures } from "@/services/api/organizationSignaturesApi"
import { format } from "date-fns"
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Check,
  Download,
  FileClock,
  FileText,
  Loader2,
  Lock,
  Send,
} from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate } from "react-router"
import { toastApiError } from "../../../api/apiError"
import {
  apiErrorDetail,
  approveRefusedErrors,
  CONFORMITY_EMPTY_REASON,
  documentTooLargeDetail,
  isComputing as isComputingState,
  scopeTooLargeDetail,
  useAcknowledgeGeneral,
  useApproveConformityV2,
  useClearFrameworkOverride,
  useConformityDeclarationFrameworks,
  useConformityDocumentBlocks,
  useConformityFrozenPdfUrl,
  useConformityPreviewHtml,
  useDownloadConformity,
  useGenerateConformityV2,
  useOverrideFramework,
  useReEvaluateConformity,
  useScipOverride,
  useSendConformity,
  useSendConformityTest,
  useSetConformityLanguage,
  useSetFrameworkDisclosure,
  useSetFrameworkSelection,
  useSetPrintProductList,
  useSetProductDescription,
  useSetProductScipNumber,
  useUpdateConformityTemplate,
  type ConformityDetail,
  type ConformityV2ApproveError,
  type ConformityV2ApproveErrorProduct,
  type ConformityV2Framework,
} from "../../../api/conformityApi"
import {
  pickableTemplates,
  useDeclarationTemplates,
} from "../../../api/declarationTemplateApi"
import { ORG_REGULATIONS_SETTINGS_ROUTE } from "../../../api/regulationSettingsApi"
import { useCanViewOrgRegulations } from "../../../hooks/useCanViewOrgRegulations"
import { dateFnsLocale } from "../../../utils/date"
import type { FilterToken } from "../../declarations/review/studio/TableSearch"
import { McPill } from "../../shared/McPill"
import { OverflowChip } from "../../shared/OverflowChip"
import {
  buildDefaultBody,
  buildDefaultSubject,
} from "./conformityCoverDefaults"
import { ConformityBlockEditorPanel } from "./editor/ConformityBlockEditorPanel"
import { ConformityPreviewPanel } from "./editor/ConformityPreviewPanel"
import { ConformityRecipientEditor } from "./editor/ConformityRecipientEditor"
import { ConformitySignatureField } from "./editor/ConformitySignatureField"
import { PdfOverlay } from "./editor/PdfOverlay"
import {
  PDF_PANE_ZOOM_DEFAULT,
  PdfPanePageIndicator,
  PdfPaneZoomControl,
  type PdfPaneZoom,
} from "./editor/PdfPaneControls"
import { SubjectChips } from "./editor/SubjectChips"
import {
  frameworkRowStatus,
  isBlockedStatus,
  scipRowAnchorId,
} from "./frameworkPresentation"
import { FrameworkResultList } from "./FrameworkResultList"
import { GeneralAcknowledgeDialog } from "./GeneralAcknowledgeDialog"
import { GeneralWarningPanel } from "./GeneralWarningPanel"
import { MIN_REASON, OverrideDialog } from "./OverrideDialog"
// The frozen PDF pane's own viewer needs `.doc-viewer`/`.doc-scroll` sizing
// and the find-bar's `.doc-bar-*` dock chrome, which live only here — no
// other file in this route loads them (see FrozenPdfPane's doc comment).
import "../../declarations/review/studioScreenA.css"

const MC_BASE = "/material-compliance"

/** A customer scope can refuse on hundreds of products; the panel shows the
 *  first few as actionable chips and the rest behind a "+N" hover. */
const REFUSAL_CHIP_CAP = 12

/** Sentinel `SelectItem` value for the header template select's last row
 *  (design §9) — never a real template key (those come from the backend's
 *  `TEMPLATE_KEY_RE`-validated slugs), so it can never collide. */
const MANAGE_TEMPLATES_VALUE = "__manage__"

// The evaluation/document split, copied from Supplier Studio v2
// (`StudioV2Layout`'s own `ResizablePanelGroup` — see its module doc): same
// primitive (`@/components/ui/resizable`, a thin wrapper over
// `react-resizable-panels`), same drag/keyboard/persistence mechanics, just a
// different `autoSaveId` and default split so this editor's saved width never
// collides with Studio's. ":v4" because react-resizable-panels 4 would read a
// v2-saved split back with the two widths swapped (it pairs v2's alphabetised
// panel ids with sizes stored in screen order), so v2 saves are left behind.
const CONFORMITY_SPLIT_STORAGE_ID = "mc-conformity-v2-split:v4"
const CONFORMITY_SPLIT_DEFAULT = 55
const CONFORMITY_SPLIT_MIN = 32
const CONFORMITY_SPLIT_MAX = 72

// General scope only (TUR-1573): how long the product description field
// waits after the last keystroke before it PATCHes.
const GENERAL_DESCRIPTION_DEBOUNCE_MS = 500

/** Customer Studio → Conformity → v2 editor (design canvas, all states).
 *
 *  Left: evaluation pane — framework rows with include checkboxes, gap
 *  trails, override, the aggregated APPROVE_REFUSED panel, Re-evaluate.
 *  Right: live document render; hovering a framework row outlines its
 *  `data-fw` block. Approve freezes the PDF; issued rows go read-only. */
export function ConformityEditorModuleV2({
  detail,
  detailIsFetching = false,
}: {
  detail: ConformityDetail
  /** Threaded through to `ConformitySignatureField` — see its own prop doc.
   *  Optional: only the signature picker needs it, and a caller that never
   *  enables ENABLE_ORG_SIGNATURES (or predates this field) can omit it. */
  detailIsFetching?: boolean
}) {
  const navigate = useNavigate()
  const { translate, currentLanguage } = useTranslation()
  const { hasPermission } = useUser()
  const flags = useGetAllFeatureFlags()
  // Flag AND permission. `useGuardedMutation` behind the buttons only logs and
  // rejects — that is a last line of defence, not access control, so a member
  // without RELEASE must not see the dialog or its test-send button at all.
  const canSendEmail =
    !!flags.MC_CONFORMITY_EMAIL_SEND &&
    hasPermission(PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE)
  // 2026-09-17 block-editor design: kill switch during rollout — off
  // everywhere until MC_CONFORMITY_BLOCK_EDITOR is seeded and flipped for an
  // org (see the alembic seed migration). Off keeps the read-only iframe
  // preview exactly as it was; the block editor stays scoped to the
  // declaration's own language (see the DE/EN toggle below).
  const blockEditorEnabled = !!flags.MC_CONFORMITY_BLOCK_EDITOR
  const canOverride = hasPermission(
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_OVERRIDE,
  )
  // Gates the own-SCIP link (FrameworkResultRow) — the mutation behind it,
  // useSetProductScipNumber, is guarded on this same permission.
  const canEditProducts = hasPermission(PERMISSIONS.MC_MASTER_DATA_EDIT)
  // Gates every draft-content mutation this module offers directly: the
  // signature picker (useUpdateConformitySignature) and the block editor's
  // autosave (useSaveConformityDocumentBlockParagraph) are both guarded on
  // this same permission server-side (useGuardedMutation is a silent last
  // line of defense, not a substitute for hiding/disabling the control at
  // the source).
  const canEditDraft = hasPermission(
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  )
  // Kill switch (TUR-1573): with MC_CONFORMITY_GENERAL off the backend
  // refuses the general-only mutations (family text, disclosure,
  // acknowledge), so an existing general draft stays readable but offers
  // none of them.
  const generalEnabled = !!flags.MC_CONFORMITY_GENERAL
  const canEditGeneral = canEditDraft && generalEnabled

  // Phase-4 template switcher (design §9): same idiom as the generate
  // screen's own picker (ConformityGenerateModuleV2) — only shown once the
  // org has two layouts to switch between (`pickableTemplates`: published
  // templates plus the built-in layout; a draft-only template that has never
  // gone live offers nothing to pin to).
  const templatesEnabled = useFeatureFlag("MC_DECLARATION_TEMPLATES")
  const templates = useDeclarationTemplates({ enabled: templatesEnabled })
  // The empty state's "open regulations" link lands on the Settings card;
  // offer it only to users who can see that card.
  const canOpenRegulations = useCanViewOrgRegulations()
  const publishedTemplates = useMemo(
    () => pickableTemplates(templates.data ?? []),
    [templates.data],
  )

  // WP2: the server recomputes this draft's conformity facts in the background,
  // so the editor can be open while the answer is still being rebuilt. Read
  // through the guard, never by comparing the raw string: the field is absent
  // on a backend that predates it and may carry a state this build has never
  // seen.
  const isComputing = isComputingState(detail.evaluationState)
  const pendingCount = detail.pendingProductCount ?? 0

  // Polled while a recompute is in flight: the worker rewrites these children,
  // so without it this panel keeps the pre-recompute numbers even after the
  // detail says "ready" (S10).
  const frameworksQuery = useConformityDeclarationFrameworks(
    detail.id,
    isComputing,
  )
  const frameworks = useMemo(
    () => frameworksQuery.data ?? detail.frameworks ?? [],
    [frameworksQuery.data, detail.frameworks],
  )
  const subjects = detail.subjects ?? []
  // General checklist (TUR-1573): the row being saved shows its target state
  // and a spinner from the click until the refetched list catches up. The
  // PATCH settles before that refetch (invalidateV2Draft does not await it),
  // so clearing on the mutation alone would flip the box back for a moment.
  const [pendingSelection, setPendingSelection] = useState<{
    frameworkKey: string
    selected: boolean
    /** The list the click saw, set once the PATCH succeeded. */
    savedOver?: ConformityV2Framework[]
  } | null>(null)
  useEffect(() => {
    if (!pendingSelection) return
    const row = frameworks.find(
      (f) => f.frameworkKey === pendingSelection.frameworkKey,
    )
    const caughtUp = !row || row.selected === pendingSelection.selected
    // Any list that arrived after the PATCH ends the wait, so a refetch that
    // disagrees can never leave the row stuck behind its spinner.
    const refetched =
      pendingSelection.savedOver !== undefined &&
      frameworks !== pendingSelection.savedOver
    if (caughtUp || refetched) setPendingSelection(null)
  }, [frameworks, pendingSelection])
  const isGeneralScope = detail.scope === "general"
  // TUR-1573: a general letter's body is not part of the block tree (the
  // backend refuses document-blocks for it), so it keeps the read-only
  // preview even with the block editor on.
  const showBlockEditor = blockEditorEnabled && !isGeneralScope
  // A draft with no framework rows is empty for one of two reasons, and the
  // copy has to name the right one. A customer draft re-resolves its products
  // from the customer mapping on every evaluation, so no subjects left means
  // the products were unlinked; otherwise the org subscribes to no regulation.
  // A general letter never has subjects (TUR-1573), so for it an empty draft
  // can only mean no regulation.
  const emptyStateKey =
    subjects.length === 0 && !isGeneralScope
      ? "MC_CONF_V2_EDIT_NO_PRODUCTS"
      : "MC_CONF_V2_EDIT_NO_FRAMEWORKS"

  const [docLang, setDocLang] = useState<"en" | "de">(
    (detail.language ?? "").toLowerCase() === "en" ? "en" : "de",
  )
  const setLanguage = useSetConformityLanguage()
  const isDraftRow = detail.status === "draft"
  // Issued rows show the frozen artefact — never fetch the live render for
  // them. With the block editor active, the live pane sources its content
  // (and title) from useConformityDocumentBlocks instead — fetching the full
  // preview HTML too would be the exact double round-trip this feature
  // exists to avoid, so it's disabled here and only the PDF-preview overlay
  // (which still supports the DE/EN toggle) keeps using it.
  const preview = useConformityPreviewHtml(
    isDraftRow ? detail.id : null,
    docLang,
    { enabled: !showBlockEditor },
  )
  const [hovered, setHovered] = useState<string | null>(null)
  const [errors, setErrors] = useState<ConformityV2ApproveError[] | null>(null)
  const [overrideFor, setOverrideFor] = useState<string | null>(null)
  const [scipFor, setScipFor] = useState<{
    productId: string
    productNumber: string | null
  } | null>(null)
  const [scipOverrideFor, setScipOverrideFor] = useState<
    ConformityV2ApproveErrorProduct[] | null
  >(null)
  // General scope only (TUR-1573): which framework's disclosure sentence is
  // being edited, and which framework keys the acknowledge dialog covers.
  const [disclosureFor, setDisclosureFor] = useState<string | null>(null)
  const [ackFor, setAckFor] = useState<string[] | null>(null)
  const [sendOpen, setSendOpen] = useState(false)
  const [splitConfirmOpen, setSplitConfirmOpen] = useState(false)
  // Armed right after a signature change; consumed by whichever document
  // pane is active on its NEXT successful load — the iframe preview
  // (`scrollToBottomOnLoad`) when the block editor flag is off, or
  // `ConformityBlockEditorPanel`'s own signature-fragment scroll when it's
  // on — not synchronous with the PATCH resolving in either case. Not armed
  // for any other edit, so an unrelated change never yanks the reviewer's
  // scroll position. Mirrors v1's ConformityEditorModule.
  const [pendingSignatureScroll, setPendingSignatureScroll] = useState(false)
  const [pdfOpen, setPdfOpen] = useState(false)
  // The frozen-PDF pane's own zoom + scroll-derived page readout — lives
  // here (not inside FrozenPdfPane) because the header row that shows them
  // sits above the pane in this same JSX, not inside the pane itself.
  const [frozenZoom, setFrozenZoom] = useState<PdfPaneZoom>(
    PDF_PANE_ZOOM_DEFAULT,
  )
  const [frozenPage, setFrozenPage] = useState<{
    current: number | null
    total: number | null
  }>({ current: null, total: null })
  // Rows the user opened or closed by hand. Every row starts open; a
  // deliberate collapse is remembered per declaration in this browser, so it
  // survives re-evaluates and reloads.
  const rowsStorageKey = `mc-conformity-v2-rows:${detail.id}`
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>(
    () => readStoredRows(rowsStorageKey),
  )
  useEffect(() => {
    try {
      window.localStorage.setItem(rowsStorageKey, JSON.stringify(expandedRows))
    } catch {
      // Storage may be unavailable (private mode); the session state still works.
    }
  }, [rowsStorageKey, expandedRows])
  const refusedRef = useRef<HTMLDivElement>(null)
  // "Signatory missing" with signatures in the library: the fix is picking
  // one in this editor's Signature field, not a trip to settings.
  const signatureFieldRef = useRef<HTMLDivElement>(null)
  const { data: ownSignatures } = useOrgSignatures(
    !!flags.ENABLE_ORG_SIGNATURES,
  )

  // The panel renders at the TOP of a scrollable list — after clicking Approve
  // from further down, "see the panel" pointed at something off-screen. Skip
  // it for a SCIP refusal: jumpToScipRow already lands the view on the row
  // that needs fixing, and the panel sits right above the framework list.
  useEffect(() => {
    if (!errors?.length) return
    if (errors.some((e) => e.code === "SCIP_REFERENCE_MISSING")) return
    refusedRef.current?.scrollIntoView({ block: "start", behavior: "smooth" })
  }, [errors])

  // Expands the REACH_SVHC row and lands the view on the missing-SCIP
  // product row (footer chip, and a refused approve with SCIP errors). The
  // row only exists once the expand has rendered, so the DOM lookup runs on
  // the next tick.
  function jumpToScipRow(productId?: string) {
    setExpandedRows((prev) => ({ ...prev, REACH_SVHC: true }))
    const targetId =
      productId ??
      frameworks.find((f) => f.frameworkKey === "REACH_SVHC")?.scipMissing[0]
    setTimeout(() => {
      const target =
        (targetId && document.getElementById(scipRowAnchorId(targetId))) ||
        document.querySelector('[data-testid="fw-row-REACH_SVHC"]')
      if (!target) return
      target.scrollIntoView?.({ behavior: "smooth", block: "center" })
      ;(target as HTMLElement).focus?.({ preventScroll: true })
    }, 0)
  }

  const setSelection = useSetFrameworkSelection()
  const printList = useSetPrintProductList()
  const override = useOverrideFramework()
  const clearOverride = useClearFrameworkOverride()
  const reEvaluate = useReEvaluateConformity()
  const approve = useApproveConformityV2()

  const download = useDownloadConformity()
  const setScip = useSetProductScipNumber()
  const scipOverride = useScipOverride()
  const generatePerProduct = useGenerateConformityV2()
  const updateTemplate = useUpdateConformityTemplate()
  const setProductDescription = useSetProductDescription()
  const setFrameworkDisclosure = useSetFrameworkDisclosure()
  const acknowledgeGeneral = useAcknowledgeGeneral()

  const isDraft = detail.status === "draft"
  const readOnly = !isDraft

  // General scope only (TUR-1573): the product family description, editable
  // inline while the row is a draft. Local state only: never re-seeded from
  // `detail.productDescription` after mount, or the PATCH's own invalidation
  // refetch would clobber a keystroke made mid-save (design section 4.3).
  const [descriptionText, setDescriptionText] = useState(
    detail.productDescription ?? "",
  )
  const debouncedDescription = useDebouncedValue(
    descriptionText,
    GENERAL_DESCRIPTION_DEBOUNCE_MS,
  )
  const lastSavedDescriptionRef = useRef(detail.productDescription ?? "")
  useEffect(() => {
    if (!isGeneralScope || !isDraft) return
    const trimmed = debouncedDescription.trim()
    // Empty is a legitimate value now: it CLEARS the family and widens the
    // letter back to all of the issuer's products. Only a non-empty text still
    // has to be a real family name, so the 3 char floor applies just to that.
    if (trimmed.length > 0 && trimmed.length < 3) return
    if (debouncedDescription === lastSavedDescriptionRef.current) return
    async function save() {
      try {
        await setProductDescription.mutateAsync({
          id: detail.id,
          // null, not '', so the PATCH clears the column instead of storing an
          // empty family the document would then have to special case.
          productDescription: trimmed ? debouncedDescription : null,
        })
        // Only a confirmed save counts; a failed one must be retried when the
        // user comes back to the same text.
        lastSavedDescriptionRef.current = debouncedDescription
      } catch (err) {
        const errorDetail = apiErrorDetail(err)
        if (errorDetail?.code === "DESCRIPTION_TAKEN") {
          toast.error(translate("MC_CS_CONF_V2_GENERAL_DESC_TAKEN"))
        } else {
          toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
        }
      }
    }
    void save()
    // setProductDescription and translate are stable across renders (the
    // mutation object and the translate callback both come from hooks that
    // do not change identity per keystroke); only the debounced text and the
    // scope/draft gate should re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDescription, isGeneralScope, isDraft, detail.id])

  // ── Derived summary (design footer line) ────────────────────────────────
  const statuses = frameworks.map(frameworkRowStatus)
  const selectable = statuses.filter((s) => s !== "unavailable").length
  const blockedCount = statuses.filter(isBlockedStatus).length
  const inDoc = frameworks.filter(
    (f, i) =>
      f.selected &&
      !isBlockedStatus(statuses[i]) &&
      statuses[i] !== "unavailable",
  ).length
  const actionsNeeded = frameworks.reduce(
    (n, f) => n + (f.frameworkKey === "REACH_SVHC" ? f.scipMissing.length : 0),
    0,
  )
  const locale = dateFnsLocale(currentLanguage)
  const fmtDate = (value: string | Date) =>
    format(new Date(value), "dd MMM yyyy", { locale })
  const fmtDateTime = (value: string | Date) =>
    format(new Date(value), "dd MMM yyyy, HH:mm", { locale })

  const summary = !isDraft
    ? translate("MC_CONF_V2_EDIT_ISSUED_SUMMARY", {
        ref: detail.reference ?? "—",
        rev: String(detail.revision ?? 1).padStart(2, "0"),
        date: detail.issuedAt ? fmtDate(detail.issuedAt) : "—",
      })
    : translate("MC_CONF_V2_EDIT_SUMMARY", {
        inDoc,
        total: selectable,
        blocked: blockedCount,
        actions: actionsNeeded,
      })

  const title =
    detail.reference ??
    (subjects.length === 1
      ? translate("MC_CONF_V2_EDIT_DRAFT_TITLE_ONE")
      : translate("MC_CONF_V2_EDIT_DRAFT_TITLE", { n: subjects.length }))
  // Design's "Nothing to issue yet": a draft where no framework can print.
  const nothingToIssue = isDraft && frameworks.length > 0 && inDoc === 0
  // The rendered document names itself. With the block editor active, that
  // title comes straight off the same document-blocks response the panel
  // itself fetches (shared cache — this doesn't add a request); with it
  // off, pulled out of the preview HTML's <title> as before. "· PDF" only
  // once it IS a PDF.
  const blocksTitleQuery = useConformityDocumentBlocks(
    showBlockEditor && isDraftRow ? detail.id : null,
  )
  const docTitle = useMemo(() => {
    const title = showBlockEditor
      ? blocksTitleQuery.data?.title?.trim()
      : preview.data?.match(/<title>([^<]*)<\/title>/)?.[1]?.trim()
    if (!title) return undefined
    return isDraft ? title : `${title} · PDF`
  }, [showBlockEditor, blocksTitleQuery.data?.title, preview.data, isDraft])

  // Hover-highlight (design delta 7), iframe path only: the stylesheet is
  // injected ONCE so the srcdoc — and with it the iframe, its scroll
  // position and selection — stays stable; hovering only toggles a class
  // inside the live document. Unused (and the query below skipped) once the
  // block editor flag is on, since [data-fw] then lives directly on the page.
  const HL_OUTLINE = "#FFF440"
  const HL_BG = "#FFFDE5"
  const previewHtml = useMemo(() => {
    if (!preview.data) return preview.data
    const style = `<style>[data-fw].fw-hl{outline:2px solid ${HL_OUTLINE};outline-offset:2px;background:${HL_BG};}</style>`
    return preview.data.replace("</head>", `${style}</head>`)
  }, [preview.data])

  const docPaneRef = useRef<HTMLDivElement>(null)
  // The block editor (flag on) renders [data-fw] directly on the page, so
  // the live DOM is queried straight through docPaneRef; the iframe (flag
  // off) needs its contentDocument instead — querySelector never pierces an
  // iframe boundary.
  const highlightRoot = useCallback((): ParentNode | null => {
    if (showBlockEditor) return docPaneRef.current
    return docPaneRef.current?.querySelector("iframe")?.contentDocument ?? null
  }, [showBlockEditor])
  const blockFor = useCallback(
    (key: string) =>
      highlightRoot()?.querySelector(`[data-fw="${CSS.escape(key)}"]`) ?? null,
    [highlightRoot],
  )

  useEffect(() => {
    if (!isDraft) return
    const root = highlightRoot()
    if (!root) return
    for (const el of root.querySelectorAll("[data-fw].fw-hl")) {
      el.classList.remove("fw-hl")
    }
    if (hovered) blockFor(hovered)?.classList.add("fw-hl")
  }, [hovered, isDraft, blockFor, highlightRoot])

  function goBack() {
    navigate(`${MC_BASE}/customer-studio/conformity`)
  }

  async function onToggleInclude(frameworkKey: string, selected: boolean) {
    if (readOnly || setSelection.isPending) return
    const clickedOver = frameworks
    setPendingSelection({ frameworkKey, selected })
    try {
      // The mutation's onSuccess invalidates frameworks + detail + list.
      await setSelection.mutateAsync({ id: detail.id, frameworkKey, selected })
      setPendingSelection((p) =>
        p && p.frameworkKey === frameworkKey
          ? { ...p, savedOver: clickedOver }
          : p,
      )
    } catch {
      setPendingSelection(null)
      toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
    }
  }

  async function onConfirmOverride(reason: string) {
    if (!overrideFor) return
    const fw = frameworks.find((f) => f.frameworkKey === overrideFor)
    try {
      await override.mutateAsync({
        id: detail.id,
        frameworkKey: overrideFor,
        reason,
      })
      setOverrideFor(null)
      toast.success(
        translate("MC_CONF_V2_OVERRIDE_TOAST", {
          name: fw?.label ?? overrideFor,
        }),
      )
    } catch (err) {
      const errorDetail = apiErrorDetail(err)
      if (errorDetail?.code === "NOT_OVERRIDABLE") {
        toast.error(translate("MC_CONF_V2_EDIT_OVERRIDE_BLOCKED_REASON"))
      } else if (errorDetail?.code === "FORBIDDEN") {
        toast.error(translate("MC_CONF_V2_EDIT_OVERRIDE_ROLE"))
      } else if (errorDetail?.code === "REASON_TOO_SHORT") {
        toast.error(translate("MC_CONF_V2_OVERRIDE_MIN"))
      } else {
        toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
      }
    }
  }

  async function onClearOverride(frameworkKey: string) {
    const fw = frameworks.find((f) => f.frameworkKey === frameworkKey)
    try {
      await clearOverride.mutateAsync({ id: detail.id, frameworkKey })
      toast.success(
        translate("MC_CONF_V2_OVERRIDE_CLEARED_TOAST", {
          name: fw?.label ?? frameworkKey,
        }),
      )
    } catch {
      toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
    }
  }

  // ── General scope only (TUR-1573): the contradiction guardrail's three
  // resolutions (design section 3/6). ─────────────────────────────────────

  async function onGeneralUntick(frameworkKey: string) {
    if (setSelection.isPending) return
    try {
      await setSelection.mutateAsync({
        id: detail.id,
        frameworkKey,
        selected: false,
      })
    } catch {
      toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
    }
  }

  async function onSaveDisclosure(text: string | null) {
    if (!disclosureFor) return
    const frameworkKey = disclosureFor
    try {
      await setFrameworkDisclosure.mutateAsync({
        id: detail.id,
        frameworkKey,
        text,
      })
      setDisclosureFor(null)
    } catch {
      toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
    }
  }

  async function onConfirmGeneralAcknowledge(reason: string) {
    if (!ackFor) return
    try {
      await acknowledgeGeneral.mutateAsync({
        id: detail.id,
        frameworkKeys: ackFor,
        reason,
      })
      setAckFor(null)
    } catch (err) {
      const errorDetail = apiErrorDetail(err)
      if (errorDetail?.code === "FORBIDDEN") {
        toast.error(translate("MC_CONF_V2_EDIT_OVERRIDE_ROLE"))
      } else {
        toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
      }
    }
  }

  /** F1/F2: both guards raise on re-evaluate and approve, not only on generate.
   *  Without this the user gets a bare "action failed" for a refusal that names
   *  its own numbers. Returns true when it handled the error. */
  function reportScaleRefusal(error: unknown): boolean {
    const tooLarge = scopeTooLargeDetail(error)
    if (tooLarge) {
      toast.error(
        translate("MC_CS_CONF_V2_SCOPE_TOO_LARGE", {
          count: String(tooLarge.count),
          limit: String(tooLarge.limit),
        }),
        {
          action: {
            label: translate("MC_CONF_V2_FIX_OPEN_SETTINGS"),
            onClick: () => navigate(SETTINGS_PATHS.conformityDeclarations),
          },
        },
      )
      return true
    }
    const tooManyRows = documentTooLargeDetail(error)
    if (tooManyRows) {
      toast.error(
        translate("MC_CS_CONF_V2_DOCUMENT_TOO_LARGE", {
          count: String(tooManyRows.count),
          limit: String(tooManyRows.limit),
        }),
      )
      return true
    }
    return false
  }

  async function onReEvaluate() {
    if (reEvaluate.isPending) return
    try {
      const result = await reEvaluate.mutateAsync(detail.id)
      setErrors((prev) =>
        prev ? prev.filter((e) => e.code !== "STALE_EVALUATION") : prev,
      )
      // "queued" means the work is on the facts queue and the detail query is
      // now polling; saying "done" there would be a lie the user can see.
      if (result.state === "queued") {
        toast.success(translate("MC_CONF_V2_EDIT_REEVALUATING_TOAST"))
      } else if (result.emptyReason) {
        // The refresh ran, but there is nothing to show. "Refreshed" here sent
        // a tester into a three-click loop (2026-09-22). Any named reason is a
        // warning; an unknown future one falls back to the frameworks copy.
        toast.warning(
          translate(
            result.emptyReason === CONFORMITY_EMPTY_REASON.noCustomerProducts
              ? "MC_CONF_V2_EDIT_NO_PRODUCTS"
              : "MC_CONF_V2_EDIT_NO_FRAMEWORKS",
          ),
        )
      } else {
        toast.success(translate("MC_CONF_V2_EDIT_REEVALUATED_TOAST"))
      }
    } catch (err) {
      if (apiErrorDetail(err)?.code === "NO_FRAMEWORKS_CONFIGURED") {
        toast.error(translate("MC_CONF_V2_EDIT_NO_FRAMEWORKS"))
        return
      }
      if (!reportScaleRefusal(err)) {
        toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
      }
    }
  }

  async function onApprove() {
    if (approve.isPending) return
    try {
      const result = await approve.mutateAsync(detail.id)
      setErrors(null)
      toast.success(
        translate("MC_CONF_V2_APPROVED_TOAST", { ref: result.reference ?? "" }),
      )
    } catch (err) {
      const refused = approveRefusedErrors(err)
      if (refused) {
        setErrors(refused)
        const scipError = refused.find(
          (e) => e.code === "SCIP_REFERENCE_MISSING" && e.products?.length,
        )
        if (scipError?.products?.length) {
          jumpToScipRow(scipError.products[0].productId)
        }
        // General scope only (TUR-1573): names the contradicting frameworks
        // directly rather than folding them into the generic "N things to
        // fix" count: the warning panel (already visible above the approve
        // button whenever a framework contradicts) is "the panel" this
        // surfaces, so no separate open/scroll action is needed here.
        const contradiction = refused.find(
          (e) => e.code === "CONTRADICTING_DATA" && e.frameworks?.length,
        )
        if (contradiction?.frameworks?.length) {
          const names = contradiction.frameworks
            .map(
              (f) =>
                frameworks.find((fw) => fw.frameworkKey === f.frameworkKey)
                  ?.label ?? f.frameworkKey,
            )
            .join(", ")
          toast.error(
            translate("MC_CONF_V2_ERR_CONTRADICTING_SHORT", {
              frameworks: names,
            }),
          )
        } else {
          toast.error(
            refused.length === 1
              ? translate("MC_CONF_V2_REFUSED_TOAST_ONE", {
                  problem: shortProblem(refused[0], translate),
                })
              : translate("MC_CONF_V2_REFUSED_TOAST_MANY", {
                  n: refused.length,
                }),
          )
        }
      } else if (!reportScaleRefusal(err)) {
        toast.error(translate("MC_CS_CONF_EDIT_TOAST_APPROVE_ERR"))
      }
    }
  }

  async function onSaveScip(scipNumber: string) {
    if (!scipFor) return
    const fixedId = scipFor.productId
    try {
      await setScip.mutateAsync({ productId: fixedId, scipNumber })
      setScipFor(null)
      await onReEvaluate()
      // Trim only the FIXED product from the refused panel — other products
      // may still be missing their number (review NF3).
      setErrors(
        (prev) =>
          prev
            ?.map((e) =>
              e.code === "SCIP_REFERENCE_MISSING"
                ? {
                    ...e,
                    products: e.products?.filter(
                      (p) => p.productId !== fixedId,
                    ),
                  }
                : e,
            )
            .filter(
              (e) =>
                e.code !== "SCIP_REFERENCE_MISSING" ||
                (e.products?.length ?? 0) > 0,
            ) ?? null,
      )
    } catch {
      toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
    }
  }

  async function onConfirmScipOverride(reason: string) {
    if (!scipOverrideFor) return
    try {
      await scipOverride.mutateAsync({
        id: detail.id,
        productIds: scipOverrideFor.map((p) => p.productId),
        reason,
      })
      setScipOverrideFor(null)
      // No re-evaluate here (unlike onSaveScip): the acknowledgement leaves the
      // snapshot unchanged, and a queued recompute would lock Approve.
      setErrors((prev) => {
        const rest = prev?.filter((e) => e.code !== "SCIP_REFERENCE_MISSING")
        return rest?.length ? rest : null
      })
      toast.success(translate("MC_CONF_V2_SCIP_OVERRIDE_TOAST"))
    } catch (err) {
      const errorDetail = apiErrorDetail(err)
      if (errorDetail?.code === "FORBIDDEN") {
        toast.error(translate("MC_CONF_V2_EDIT_OVERRIDE_ROLE"))
      } else if (errorDetail?.code === "REASON_TOO_SHORT") {
        toast.error(translate("MC_CONF_V2_OVERRIDE_MIN"))
      } else {
        toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
      }
    }
  }

  function onSignatureChanged(signatureId: string | null) {
    setPendingSignatureScroll(true)
    // Applying a signature fixes SIGNATORY_MISSING, so trim it from the
    // refused panel the same way a saved SCIP number trims its product.
    // Removing one adds nothing back: the panel only ever reflects the last
    // approve attempt, and the next approve will report it again.
    if (signatureId) {
      setErrors((prev) => {
        const kept = prev?.filter((e) => e.code !== "SIGNATORY_MISSING")
        return kept?.length ? kept : null
      })
    }
  }

  // Any deselection the user made on purpose (as opposed to a framework
  // that is unselected because it's blocked) or any override does not carry
  // over to the fresh per-product evaluations — each product is scored from
  // scratch (review F12).
  const hasUnsavedDraftChanges = frameworks.some(
    (f) =>
      (f.selected === false && f.blockedReason === null) || !!f.overrideReason,
  )

  async function runPerProduct() {
    // TUR-1733: the per-product drafts declare this draft's regulations, unticked
    // ones included (re-evaluate keeps the same set). A draft with no framework
    // rows sends none, so the server falls back to the org set.
    const regulationKeys = frameworks.map((f) => f.frameworkKey)
    try {
      const { declarationIds } = await generatePerProduct.mutateAsync({
        scope: "products",
        subjectIds: subjects.map((s) => s.subjectId),
        onePerSubject: true,
        ...(regulationKeys.length > 0 ? { regulationKeys } : {}),
      })
      // Same convention as Generate: land in the first split draft's editor
      // with the batch pager.
      const batch =
        declarationIds.length > 1 ? `?batch=${declarationIds.join(",")}` : ""
      navigate(
        `${MC_BASE}/customer-studio/conformity/edit/${declarationIds[0]}${batch}`,
      )
    } catch {
      toast.error(translate("MC_CS_CONF_GEN_TOAST_ERR"))
    }
  }

  async function onTogglePrintProductList() {
    if (readOnly || printList.isPending) return
    try {
      // Only the PDF changes, so the preview refetch (via invalidation) is the
      // whole feedback — no toast for a switch whose result is visible.
      await printList.mutateAsync({
        id: detail.id,
        printProductList: !detail.printProductList,
      })
    } catch {
      toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
    }
  }

  // The header select's own "Manage templates…" row navigates instead of
  // mutating — it is a `SelectItem` sharing the same `onValueChange`, not a
  // separate control, so this is where the two paths split.
  async function onTemplateChange(value: string) {
    if (value === MANAGE_TEMPLATES_VALUE) {
      navigate(SETTINGS_PATHS.conformityDeclarations)
      return
    }
    if (readOnly || updateTemplate.isPending || value === detail.templateKey) {
      return
    }
    try {
      await updateTemplate.mutateAsync({ id: detail.id, templateKey: value })
    } catch (err) {
      toastApiError(err, translate, "MC_CONF_V2_EDIT_TEMPLATE_ERR")
    }
  }

  async function onPerProduct() {
    // Design: the header toggle splits a products-scope draft into one
    // document per product (generate is idempotent per subject_key).
    if (readOnly || generatePerProduct.isPending || subjects.length < 2) return
    if (hasUnsavedDraftChanges) {
      setSplitConfirmOpen(true)
      return
    }
    await runPerProduct()
  }

  async function onConfirmSplit() {
    setSplitConfirmOpen(false)
    await runPerProduct()
  }

  async function onDownload() {
    try {
      await download.mutateAsync([detail.id])
      toast.success(translate("MC_CS_CONF_EDIT_TOAST_DOWNLOAD_OK"))
    } catch {
      toast.error(translate("MC_CS_CONF_EDIT_TOAST_DOWNLOAD_ERR"))
    }
  }

  const overrideFramework = overrideFor
    ? (frameworks.find((f) => f.frameworkKey === overrideFor) ?? null)
    : null

  // Phase-4 template switcher (design §9), in the document's own toolbar
  // next to its title: only once there are two layouts to switch between,
  // and only for a member who can actually edit the draft — every sibling
  // control that drives a GENERATE-guarded mutation (the signature picker,
  // the print-list switch) is hidden at the render source the same way,
  // rather than left enabled to fail silently at the guarded mutation.
  const templateSwitcher =
    templatesEnabled &&
    isDraft &&
    canEditDraft &&
    publishedTemplates.length > 1 ? (
      <Select
        value={detail.templateKey ?? undefined}
        onValueChange={onTemplateChange}
        disabled={updateTemplate.isPending}
      >
        <SelectTrigger
          className="h-7 w-auto max-w-64 min-w-40 gap-1.5 text-[12px]"
          aria-label={translate("MC_CONF_V2_EDIT_TEMPLATE")}
        >
          {/* Radix mirrors the selected item's full rendered content
                      (name + badge) into the closed trigger by default —
                      explicit children here keep the closed trigger to just
                      the name, so it doesn't compete with the badge for the
                      same clipped line. The badge stays on the open list
                      items below. */}
          <SelectValue>
            <span className="truncate">{detail.templateName}</span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {publishedTemplates.map((t) => (
            <SelectItem key={t.key} value={t.key}>
              <span className="flex items-center gap-2">
                {t.name}
                {t.isDefault && (
                  <Badge
                    variant="outline"
                    className="px-1.5 py-0 text-[10px] font-normal"
                  >
                    {translate("MC_DECL_TPL_CARD_DEFAULT")}
                  </Badge>
                )}
              </span>
            </SelectItem>
          ))}
          <SelectSeparator />
          <SelectItem value={MANAGE_TEMPLATES_VALUE}>
            {translate("MC_CONF_V2_EDIT_TEMPLATE_MANAGE")}
          </SelectItem>
        </SelectContent>
      </Select>
    ) : null

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* ─── Header ─── */}
      <div className="flex flex-col gap-2 border-b border-grey4 bg-grey2 px-6 py-2.5">
        <div className="flex items-center gap-2 text-[12px] text-grey5">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-1.5 text-[12px]"
            onClick={goBack}
          >
            <ArrowLeft size={13} className="mr-1" />
            {translate("MC_CS_CONF_EDIT_BACK")}
          </Button>
          <span className="ml-auto" />
          <StatusPillV2 status={detail.status} translate={translate} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-[18px] font-semibold tracking-tight text-white">
            {title}
          </h1>
          <SubjectChips
            customerName={detail.customerName}
            subjects={subjects}
          />
          <span className="flex-1" />
          {isDraft && (
            <div className="inline-flex items-center rounded-md border border-grey4 p-0.5">
              {(["de", "en"] as const).map((lang) => (
                <button
                  key={lang}
                  type="button"
                  // Serialised, not just guarded on the result: two PATCHes in
                  // flight can land out of order and leave the stored language
                  // disagreeing with the preview the reviewer approved from.
                  disabled={setLanguage.isPending || lang === docLang}
                  onClick={async () => {
                    // Persist, don't just re-render: the issued PDF follows the
                    // STORED language, so a preview-only switch shipped the
                    // wrong one. And the preview must not run ahead of the row:
                    // a failed PATCH would leave the editor showing German
                    // while approve froze English, with nothing on screen
                    // saying so. Roll back and say it failed instead.
                    const previous = docLang
                    setDocLang(lang)
                    try {
                      await setLanguage.mutateAsync({
                        id: detail.id,
                        language: lang,
                      })
                    } catch (error) {
                      setDocLang(previous)
                      toastApiError(
                        error,
                        translate,
                        "MC_CONF_V2_LANG_SET_FAILED",
                      )
                    }
                  }}
                  className={cn(
                    "rounded px-2 py-0.5 text-[11px] font-medium",
                    docLang === lang
                      ? "bg-white/10 text-white"
                      : "text-grey5 hover:text-white",
                  )}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
          )}
          {/* F3/F4: a real switch, not a div that looks like one — and hidden
              for members who cannot edit the draft, since the guarded mutation
              would otherwise no-op in silence. */}
          {isDraft && detail.scope === "customer" && canEditDraft && (
            <label className="inline-flex cursor-pointer items-center gap-2 text-[12px] text-grey5 hover:text-white">
              <Switch
                checked={detail.printProductList}
                disabled={printList.isPending}
                onCheckedChange={onTogglePrintProductList}
                aria-label={translate("MC_CONF_V2_EDIT_PRINT_LIST")}
              />
              <span>
                {translate("MC_CONF_V2_EDIT_PRINT_LIST")}
                <span className="block text-[11px] text-grey5">
                  {translate("MC_CONF_V2_EDIT_PRINT_LIST_HINT", {
                    count: String(subjects.length),
                  })}
                </span>
              </span>
            </label>
          )}
          {isDraft && detail.scope !== "customer" && subjects.length > 1 && (
            <button
              type="button"
              onClick={onPerProduct}
              disabled={generatePerProduct.isPending}
              className="inline-flex items-center gap-2 border-0 bg-transparent p-0 text-[12px] text-grey5 hover:text-white"
            >
              {generatePerProduct.isPending ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <span className="relative inline-block h-4 w-7 rounded-full bg-grey4">
                  <span className="absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white" />
                </span>
              )}
              {translate("MC_CS_CONF_V2_ONE_PER_PRODUCT")}
            </button>
          )}
        </div>
      </div>

      {/* ─── Body split ─── */}
      {/* Draggable, like Supplier Studio v2's own evaluation|document split
          (`StudioV2Layout`'s `ResizablePanelGroup`) — same primitive, same
          drag/keyboard/persistence mechanics, its own `autoSaveId` so the
          two screens never fight over one saved width. */}
      <ResizablePanelGroup
        orientation="horizontal"
        autoSaveId={CONFORMITY_SPLIT_STORAGE_ID}
        className="min-h-0 flex-1"
      >
        {/* LEFT — evaluation */}
        <ResizablePanel
          id="evaluation"
          defaultSize={CONFORMITY_SPLIT_DEFAULT}
          minSize={CONFORMITY_SPLIT_MIN}
          maxSize={CONFORMITY_SPLIT_MAX}
        >
          <section className="flex h-full min-w-0 flex-col border-r border-grey4">
            <div className="flex h-11 flex-none items-center gap-2.5 border-b border-white/[0.07] px-4">
              <span className="text-[12.5px] font-semibold text-grey5">
                {translate("MC_CONF_V2_EDIT_EVALUATION")}
              </span>
              {detail.generatedAt && (
                <span className="font-mono text-[11px] text-grey5">
                  {translate("MC_CONF_V2_EDIT_EVALUATED_AT", {
                    date: fmtDateTime(detail.generatedAt),
                  })}
                </span>
              )}
              <span className="flex-1" />
              {isDraft && (
                <button
                  type="button"
                  onClick={onReEvaluate}
                  disabled={reEvaluate.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md border border-grey4 px-2.5 py-1 text-[11.5px] font-medium text-grey5 hover:border-grey5 hover:text-white"
                >
                  {reEvaluate.isPending && (
                    <Loader2 size={11} className="animate-spin" />
                  )}
                  {translate("MC_CONF_V2_EDIT_REEVALUATE")}
                </button>
              )}
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 pt-3.5 pb-6">
              {errors && errors.length > 0 && (
                <RefusedPanel
                  panelRef={refusedRef}
                  errors={errors}
                  onDismiss={() => setErrors(null)}
                  onReEvaluate={onReEvaluate}
                  onOpenScip={(p) => setScipFor(p)}
                  onScipOverride={
                    canOverride
                      ? (products) => setScipOverrideFor(products)
                      : undefined
                  }
                  canEditProducts={canEditProducts}
                  onChooseSignature={
                    isDraft && canEditDraft && !!ownSignatures?.length
                      ? () => {
                          const field = signatureFieldRef.current
                          field?.scrollIntoView?.({
                            behavior: "smooth",
                            block: "center",
                          })
                          field
                            ?.querySelector<HTMLButtonElement>(
                              "button:not(:disabled)",
                            )
                            ?.focus({ preventScroll: true })
                        }
                      : undefined
                  }
                  translate={translate}
                />
              )}

              {isGeneralScope && (
                <GeneralDescriptionField
                  value={descriptionText}
                  onChange={setDescriptionText}
                  // PATCH /product-description is guarded on GENERATE.
                  disabled={!isDraft || !canEditGeneral}
                  translate={translate}
                />
              )}

              {reEvaluate.isPending && frameworks.length === 0 ? (
                <EvaluatingSkeleton count={4} translate={translate} />
              ) : frameworksQuery.isLoading && frameworks.length === 0 ? (
                <div className="flex items-center gap-2 text-[12px] text-grey5">
                  <Loader2 size={12} className="animate-spin" />
                  {translate("MC_CS_CONF_EDIT_LOADING")}
                </div>
              ) : frameworks.length === 0 && !isComputing ? (
                // The persisted draft has no framework children. Say why instead
                // of rendering an empty container.
                <div
                  data-testid="conf-edit-empty"
                  className="rounded-md border border-grey4 bg-grey2 p-4 text-[13px] text-white"
                >
                  <p>{translate(emptyStateKey)}</p>
                  {canOpenRegulations &&
                    emptyStateKey === "MC_CONF_V2_EDIT_NO_FRAMEWORKS" && (
                      <Link
                        to={ORG_REGULATIONS_SETTINGS_ROUTE}
                        className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-yellow1 hover:underline"
                      >
                        {translate("MC_CONF_V2_OPEN_REGULATIONS")}
                        <ArrowRight size={11} />
                      </Link>
                    )}
                </div>
              ) : (
                // A re-evaluate keeps the rows on screen (dimmed) instead of
                // swapping in a skeleton: no layout jump, no lost scroll.
                <div
                  aria-busy={reEvaluate.isPending}
                  className={cn(
                    "flex flex-col gap-3 transition-opacity",
                    reEvaluate.isPending && "pointer-events-none opacity-50",
                  )}
                >
                  {isGeneralScope ? (
                    // General scope (design section 3): every framework is
                    // selectable, with no gap lists, detail tables or blocked
                    // pills, since missing supplier data is irrelevant to a letter
                    // that makes no per-product claim.
                    <GeneralFrameworkChecklist
                      frameworks={frameworks}
                      // PATCH /frameworks/{key} is guarded on GENERATE.
                      readOnly={readOnly || !canEditDraft}
                      pending={pendingSelection}
                      savingLabel={translate("MC_DECL_CONFIRM_SAVING")}
                      onToggleInclude={onToggleInclude}
                    />
                  ) : (
                    <FrameworkResultList
                      frameworks={frameworks}
                      readOnly={readOnly}
                      canOverride={canOverride}
                      canEditProducts={canEditProducts}
                      expanded={expandedRows}
                      onToggleExpand={(key, next) =>
                        setExpandedRows((prev) => ({ ...prev, [key]: next }))
                      }
                      onToggleInclude={onToggleInclude}
                      onHover={setHovered}
                      onOverride={(key) => setOverrideFor(key)}
                      onClearOverride={onClearOverride}
                      onAddScip={(productId, productNumber) =>
                        setScipFor({ productId, productNumber })
                      }
                    />
                  )}
                  {isGeneralScope && (
                    <GeneralWarningPanel
                      frameworks={frameworks}
                      // Untick and disclose are guarded on GENERATE
                      // server-side; undefined hides them for a view-only
                      // member, like onAcknowledge below.
                      onUntick={canEditDraft ? onGeneralUntick : undefined}
                      onDisclose={
                        canEditGeneral
                          ? (key) => setDisclosureFor(key)
                          : undefined
                      }
                      // MC_CUSTOMER_DECLARATIONS_OVERRIDE gates POST
                      // /general-acknowledge server-side; undefined hides the
                      // action instead of letting it fail after a typed
                      // reason (same mechanism as RefusedPanel's
                      // onScipOverride below). Untick and disclose need no
                      // special permission, so they stay available either way.
                      onAcknowledge={
                        canOverride && generalEnabled
                          ? (key) => setAckFor([key])
                          : undefined
                      }
                    />
                  )}
                </div>
              )}

              {/* Only while draft: once approved the document is a frozen
                  snapshot (FrozenPdfPane, right pane) that a signature change
                  can no longer affect, so showing the picker here would let
                  a click PATCH successfully with nothing visibly happening. */}
              {isDraft && canEditDraft && (
                <div ref={signatureFieldRef}>
                  <ConformitySignatureField
                    detail={detail}
                    detailIsFetching={detailIsFetching}
                    onSignatureChanged={onSignatureChanged}
                  />
                </div>
              )}
            </div>

            {/* Footer action bar — summary on its own line so it never truncates */}
            <footer className="flex flex-none flex-col gap-1.5 border-t border-grey4 bg-grey2 px-4 py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[12px] text-grey5">{summary}</span>
                {isComputing && (
                  // Says WHY the document is not releasable yet, with the count,
                  // rather than leaving a disabled Approve unexplained.
                  // `info`, not `attention`: the SCIP pill below demands a
                  // fix from the user, this one only reports that the server is
                  // busy and there is nothing to act on.
                  <McPill tone="info" icon={Loader2}>
                    {translate("MC_CONF_V2_COMPUTING_PILL", {
                      n: pendingCount,
                    })}
                  </McPill>
                )}
                {isDraft && actionsNeeded > 0 && (
                  // Says what will block the approve BEFORE it is clicked, and
                  // jumps to the row that owns it.
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0"
                    onClick={() => jumpToScipRow()}
                  >
                    <McPill tone="attention" icon={AlertCircle}>
                      {translate("MC_CONF_V2_ACTION_PENDING_SCIP", {
                        n: actionsNeeded,
                      })}
                    </McPill>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2.5">
                <span className="flex-1" />
                {isDraft ? (
                  <Button
                    data-agent-target="conformity.approve"
                    size="sm"
                    className="h-8 bg-yellow1 text-[12.5px] font-semibold text-black hover:bg-yellow2 disabled:opacity-40"
                    // Blocked while a recompute is in flight. NOT because the
                    // server refuses it: `_evaluate` falls back to a live walk,
                    // so approve would succeed. It is blocked because the
                    // numbers on screen are about to change, and approving the
                    // ones being replaced is a decision nobody meant to take.
                    disabled={approve.isPending || inDoc === 0 || isComputing}
                    title={
                      isComputing
                        ? translate("MC_CONF_V2_COMPUTING_APPROVE_BLOCKED")
                        : undefined
                    }
                    onClick={onApprove}
                  >
                    {approve.isPending ? (
                      <Loader2 size={12} className="mr-1.5 animate-spin" />
                    ) : null}
                    {translate("MC_CONF_V2_EDIT_APPROVE")}
                    <Check size={12} className="ml-1" />
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-[12.5px]"
                      disabled={download.isPending}
                      onClick={onDownload}
                    >
                      <Download size={12} className="mr-1.5" />
                      {translate("MC_CS_CONF_REVIEW_DOWNLOAD")}
                    </Button>
                    {canSendEmail && (
                      <Button
                        data-agent-target="conformity.send"
                        size="sm"
                        className="h-8 bg-yellow1 text-[12.5px] font-semibold text-black hover:bg-yellow2"
                        onClick={() => setSendOpen(true)}
                      >
                        {translate("MC_CS_CONF_EDIT_SEND_EMAIL")}
                        <Send size={12} className="ml-1" />
                      </Button>
                    )}
                  </>
                )}
              </div>
            </footer>
          </section>
        </ResizablePanel>

        {/* Same handle the rest of Customer Studio uses (GapsStep,
            MasterDataPanelLayout) — the shared component's own look, not
            Studio v2's yellow-line variant (studioV2.css isn't loaded
            here). Double-click resets to the default split (built into
            react-resizable-panels 4: it restores the panels' defaultSize). */}
        <ResizableHandle withHandle disableDoubleClick={false} />

        {/* RIGHT — document. The preview panel brings its own toolbar (zoom +
            download), so the frozen badge rides along instead of a second row. */}
        <ResizablePanel
          id="document"
          defaultSize={100 - CONFORMITY_SPLIT_DEFAULT}
          minSize={100 - CONFORMITY_SPLIT_MAX}
        >
          <section className="flex h-full min-w-0 flex-col">
            {!isDraft ? (
              <>
                <div className="flex h-11 flex-none items-center gap-2.5 border-b border-white/[0.07] px-4">
                  <span className="text-[12.5px] font-semibold text-grey5">
                    {translate("MC_CONF_V2_EDIT_DOCUMENT")}
                  </span>
                  <PdfPanePageIndicator
                    current={frozenPage.current}
                    total={frozenPage.total}
                  />
                  <span className="flex-1" />
                  <PdfPaneZoomControl
                    zoom={frozenZoom}
                    onChange={setFrozenZoom}
                  />
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-grey5">
                    <Lock size={11} />
                    {translate("MC_CONF_V2_EDIT_FROZEN")}
                  </span>
                </div>
                <FrozenPdfPane
                  declarationId={detail.id}
                  translate={translate}
                  scale={frozenZoom === 1 ? undefined : frozenZoom}
                  onCurrentPage={(current, total) =>
                    setFrozenPage({ current, total })
                  }
                />
              </>
            ) : nothingToIssue ? (
              <EmptyDocState translate={translate} blocked={blockedCount > 0} />
            ) : showBlockEditor ? (
              // 2026-09-17 block-editor design: a live draft gets the
              // editable Tiptap panel instead of the read-only iframe —
              // paragraph wording is edited here directly, autosaved in the
              // background. Frozen/issued rows above never reach this branch.
              <ConformityBlockEditorPanel
                declarationId={detail.id}
                className="flex min-h-0 w-full flex-1 flex-col"
                containerRef={docPaneRef}
                label={docTitle}
                headerExtra={templateSwitcher}
                onPreviewPdf={() => setPdfOpen(true)}
                previewPdfLabel={translate("MC_CONF_V2_EDIT_PREVIEW_PDF")}
                onDownload={onDownload}
                downloadPending={download.isPending}
                scrollToSignatureOnLoad={pendingSignatureScroll}
                onScrolledToSignature={() => setPendingSignatureScroll(false)}
                // Same permission the autosave PATCH is guarded by
                // (server-side and via useGuardedMutation) and the same one
                // ConformitySignatureField gates on — without this, a member
                // lacking it could type into the document and only find out
                // it never saved when the autosave silently no-ops.
                canEdit={canEditDraft}
              />
            ) : (
              <ConformityPreviewPanel
                containerRef={docPaneRef}
                className="flex min-h-0 w-full flex-1 flex-col"
                hideHint
                fitToWidth
                showDownload={false}
                label={docTitle}
                headerExtra={templateSwitcher}
                isRefetching={preview.isFetching}
                onPreviewPdf={() => setPdfOpen(true)}
                previewPdfLabel={translate("MC_CONF_V2_EDIT_PREVIEW_PDF")}
                html={previewHtml}
                isLoading={preview.isLoading}
                isError={preview.isError}
                keyId={detail.id}
                onDownload={onDownload}
                downloadPending={download.isPending}
                scrollToBottomOnLoad={pendingSignatureScroll}
                onScrolledToBottom={() => setPendingSignatureScroll(false)}
              />
            )}
            <div className="flex flex-none items-center gap-1.5 border-t border-white/[0.07] px-4 py-1.5 text-[10.5px] text-grey5">
              <AlertCircle size={11} />
              {isDraft ? (
                templatesEnabled ? (
                  <span>
                    {translate("MC_CONF_V2_EDIT_DOC_HINT_LIVE")}{" "}
                    <Link
                      to={SETTINGS_PATHS.conformityDeclarations}
                      className="underline hover:text-white"
                    >
                      {translate("MC_CONF_V2_EDIT_DOC_HINT_LINK")}
                    </Link>
                  </span>
                ) : (
                  // No settings link to send them to, so nothing follows the
                  // colon the linked copy ends on: drop it. (Half-width for
                  // EN/DE, full-width for JA — and no ASCII "." appended, which
                  // was wrong in Japanese either way.)
                  translate("MC_CONF_V2_EDIT_DOC_HINT_LIVE").replace(
                    /\s*[:：]\s*$/,
                    "",
                  )
                )
              ) : (
                translate("MC_CONF_V2_EDIT_DOC_HINT_FROZEN")
              )}
            </div>
          </section>
        </ResizablePanel>
      </ResizablePanelGroup>

      {pdfOpen && (
        <PdfOverlay
          declarationId={detail.id}
          lang={docLang}
          title={docTitle}
          onClose={() => setPdfOpen(false)}
          // A draft's download icon must never fire the bundle-download
          // mutation — that marks the DRAFT as DOWNLOADED and dead-ends the
          // approve flow. Only an issued row gets the icon.
          onDownload={isDraft ? undefined : onDownload}
          downloadPending={download.isPending}
        />
      )}

      {overrideFramework && (
        <OverrideDialog
          framework={overrideFramework}
          pending={override.isPending}
          onConfirm={onConfirmOverride}
          onClose={() => setOverrideFor(null)}
        />
      )}

      {disclosureFor && (
        <GeneralDisclosureDialog
          framework={
            frameworks.find((f) => f.frameworkKey === disclosureFor) ?? null
          }
          pending={setFrameworkDisclosure.isPending}
          onSave={onSaveDisclosure}
          onClose={() => setDisclosureFor(null)}
        />
      )}

      {ackFor && (
        <GeneralAcknowledgeDialog
          frameworkKeys={ackFor}
          pending={acknowledgeGeneral.isPending}
          onConfirm={onConfirmGeneralAcknowledge}
          onClose={() => setAckFor(null)}
        />
      )}

      {scipFor && (
        <ScipDialog
          productNumber={scipFor.productNumber}
          pending={setScip.isPending}
          onSave={onSaveScip}
          onClose={() => setScipFor(null)}
          overrideWarning={
            frameworks.some((f) => f.overrideReason)
              ? translate("MC_CONF_V2_SCIP_OVERRIDE_WARNING")
              : null
          }
        />
      )}

      {scipOverrideFor && (
        <ScipOverrideDialog
          products={scipOverrideFor}
          pending={scipOverride.isPending}
          onConfirm={onConfirmScipOverride}
          onClose={() => setScipOverrideFor(null)}
        />
      )}

      {sendOpen && (
        <SendDialogV2 detail={detail} onClose={() => setSendOpen(false)} />
      )}

      {splitConfirmOpen && (
        <SplitConfirmDialog
          pending={generatePerProduct.isPending}
          onConfirm={onConfirmSplit}
          onClose={() => setSplitConfirmOpen(false)}
        />
      )}
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────────────────────

type Translate = ReturnType<typeof useTranslation>["translate"]

/** One-line version of a refusal, for the toast — "see the panel" made the
 *  reader hunt for it. */
function shortProblem(
  error: ConformityV2ApproveError,
  translate: Translate,
): string {
  switch (error.code) {
    case "SCIP_REFERENCE_MISSING":
      return translate("MC_CONF_V2_ERR_SCIP_SHORT", {
        products: error.products?.map((p) => p.productNumber).join(", ") ?? "",
      })
    case "SIGNATORY_MISSING":
      return translate("MC_CONF_V2_ERR_SIGNATORY_SHORT")
    case "NO_FRAMEWORK":
      return translate("MC_CONF_V2_ERR_NO_FRAMEWORK_SHORT")
    case "CONTRADICTING_DATA":
      // General scope only (TUR-1573). `onApprove` shows a bespoke toast
      // naming the frameworks by their editor label before falling back to
      // this generic path, so this branch only fires for the "many errors"
      // count-toast case, or if it is ever reached without a frameworks list.
      return translate("MC_CONF_V2_ERR_CONTRADICTING_SHORT", {
        frameworks:
          error.frameworks?.map((f) => f.frameworkKey).join(", ") ?? "",
      })
    case "PLACE_MISSING":
      return translate("MC_CONF_V2_ERR_PLACE")
    case "DOCUMENT_TOO_LARGE":
      return translate("MC_CONF_V2_ERR_DOCUMENT_TOO_LARGE")
    default:
      return translate("MC_CONF_V2_ERR_STALE_SHORT")
  }
}

function readStoredRows(key: string): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(key)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return Object.fromEntries(
        Object.entries(parsed as Record<string, unknown>).filter(
          ([, v]) => typeof v === "boolean",
        ),
      ) as Record<string, boolean>
    }
  } catch {
    // ignore corrupt or unavailable storage
  }
  return {}
}

function StatusPillV2({
  status,
  translate,
}: {
  status: ConformityDetail["status"]
  translate: Translate
}) {
  const issued = status !== "draft"
  return (
    <McPill tone={issued ? "success" : "muted"}>
      {issued
        ? translate("MC_CONF_V2_EDIT_PILL_ISSUED")
        : translate("MC_CONF_V2_EDIT_PILL_DRAFT")}
    </McPill>
  )
}

/** General scope only (TUR-1573): the product family description, editable
 *  inline while the row is a draft (design section 4.3). The caller owns the
 *  debounce and the PATCH; this is a plain controlled field. */
function GeneralDescriptionField({
  value,
  onChange,
  disabled,
  translate,
}: {
  value: string
  onChange: (value: string) => void
  disabled: boolean
  translate: Translate
}) {
  return (
    <div className="shrink-0">
      <label
        htmlFor="general-product-description"
        className="mb-1.5 block text-[10.5px] font-semibold tracking-widest text-grey5 uppercase"
      >
        {translate("MC_CS_CONF_V2_GENERAL_DESCRIPTION_LABEL")}
      </label>
      <textarea
        id="general-product-description"
        rows={2}
        maxLength={500}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={translate("MC_CS_CONF_V2_GENERAL_DESCRIPTION_HINT")}
        className="w-full resize-y rounded-md border border-grey4 bg-grey3 px-3 py-2 text-[12.5px] text-white outline-hidden focus:border-yellow1 disabled:opacity-60"
      />
      {/* Empty is legitimate (all products); 1 or 2 characters saves nothing, so
          say why instead of leaving the field looking accepted. */}
      {value.trim().length > 0 && value.trim().length < 3 && (
        <p
          data-testid="general-description-too-short"
          className="mt-1 text-[11px] text-warning"
        >
          {translate("MC_CS_CONF_V2_GENERAL_DESCRIPTION_TOO_SHORT")}
        </p>
      )}
    </div>
  )
}

/** General scope only (TUR-1573, design section 3): every framework renders
 *  as a plain, always-selectable checkbox: no gap trail, no per-framework
 *  detail table, no blocked pill. Missing supplier data is irrelevant to a
 *  letter that makes no per-product claim, so `blockedReason`/`gaps` are
 *  deliberately never read here. */
function GeneralFrameworkChecklist({
  frameworks,
  readOnly,
  pending,
  savingLabel,
  onToggleInclude,
}: {
  frameworks: ConformityV2Framework[]
  readOnly: boolean
  /** The selection being saved: that row shows its target state, a spinner
   *  and no further clicks until the refetched list agrees. */
  pending: { frameworkKey: string; selected: boolean } | null
  savingLabel: string
  onToggleInclude: (frameworkKey: string, selected: boolean) => void
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {frameworks.map((f) => {
        const blocked = !!f.blockedReason
        const saving = pending?.frameworkKey === f.frameworkKey
        const checkboxId = `general-fw-${f.frameworkKey}`
        return (
          <label
            key={f.frameworkKey}
            htmlFor={checkboxId}
            className="flex items-center gap-2.5 rounded-md border border-white/[0.07] px-2.5 py-2 text-[12.5px] text-white"
          >
            <Checkbox
              id={checkboxId}
              data-testid={checkboxId}
              // TUR-1590 stores blocked rows selected; the backend refuses
              // toggling one (409 BLOCKED) and the document leaves it out.
              checked={saving ? !!pending?.selected : f.selected && !blocked}
              disabled={readOnly || blocked || saving}
              onCheckedChange={(value) =>
                onToggleInclude(f.frameworkKey, value === true)
              }
            />
            <span>{f.label}</span>
            {saving && (
              <span
                role="status"
                aria-label={savingLabel}
                data-testid={`general-fw-pending-${f.frameworkKey}`}
              >
                <Loader2 size={12} className="animate-spin text-grey5" />
              </span>
            )}
          </label>
        )
      })}
    </div>
  )
}

/** General scope only (TUR-1573): the "add disclosure" action's dialog,
 *  the printed sentence naming what this framework's clean statement does
 *  not cover (design section 6.5). `null` framework guards a stale key (the
 *  frameworks list refetched while the dialog was open); the caller closes
 *  it in that case. Uses the shared `Dialog` primitive for the same reason
 *  `GeneralAcknowledgeDialog` mirrors `OverrideDialog`: no new pattern for a
 *  single-sentence editor. */
function GeneralDisclosureDialog({
  framework,
  pending,
  onSave,
  onClose,
}: {
  framework: ConformityV2Framework | null
  pending: boolean
  onSave: (text: string | null) => void
  onClose: () => void
}) {
  const { translate } = useTranslation()
  const [text, setText] = useState(framework?.disclosureText ?? "")

  if (!framework) return null

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {translate("MC_CS_CONF_V2_GENERAL_WARN_DISCLOSE")}
            <span className="ml-2 text-[13px] font-normal text-grey5">
              {framework.label}
            </span>
          </DialogTitle>
        </DialogHeader>
        <textarea
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={translate(
            "MC_CS_CONF_V2_GENERAL_DISCLOSURE_PLACEHOLDER",
          )}
          aria-label={translate("MC_CS_CONF_V2_GENERAL_DISCLOSURE_PLACEHOLDER")}
          className="w-full resize-y rounded-md border border-grey4 bg-grey3 px-3 py-2 text-[12.5px] text-white outline-hidden focus:border-yellow1"
        />
        <DialogFooter>
          <Button
            variant="outline"
            className="h-9 text-[13px]"
            onClick={onClose}
          >
            {translate("MC_CS_CONF_EDIT_CANCEL")}
          </Button>
          {framework.disclosureText && (
            <Button
              variant="outline"
              className="h-9 text-[13px]"
              disabled={pending}
              onClick={() => onSave(null)}
            >
              {translate("MC_CS_CONF_V2_GENERAL_DISCLOSURE_CLEAR")}
            </Button>
          )}
          <Button
            className="h-9 bg-yellow1 text-[13px] font-semibold text-black hover:bg-yellow2 disabled:opacity-40"
            disabled={text.trim().length === 0 || pending}
            onClick={() => onSave(text.trim())}
          >
            {pending && <Loader2 size={13} className="mr-1.5 animate-spin" />}
            {translate("MC_CS_CONF_V2_GENERAL_DISCLOSURE_SAVE")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The aggregated 409 panel (design): one row per failed gate, each with its
 *  fix action; SCIP rows list the products as clickable chips. */
function RefusedPanel({
  panelRef,
  errors,
  onDismiss,
  onReEvaluate,
  onOpenScip,
  onScipOverride,
  canEditProducts,
  onChooseSignature,
  translate,
}: {
  panelRef: React.Ref<HTMLDivElement>
  errors: ConformityV2ApproveError[]
  onDismiss: () => void
  onReEvaluate: () => void
  onOpenScip: (p: { productId: string; productNumber: string | null }) => void
  /** Continue without the own SCIP number. Undefined without
   *  MC_CUSTOMER_DECLARATIONS_OVERRIDE: the action is hidden, not refused. */
  onScipOverride?: (products: ConformityV2ApproveErrorProduct[]) => void
  /** Without MC_MASTER_DATA_EDIT the product chips are labels, not links:
   *  the SCIP save behind them would be refused anyway. */
  canEditProducts: boolean
  /** Brings the editor's Signature field into view; set when the user has
   *  signatures to pick from there. */
  onChooseSignature?: () => void
  translate: Translate
}) {
  const navigate = useNavigate()
  const flags = useGetAllFeatureFlags()
  const inquirySettings = useInquirySettingsAccess()
  // With signatures, the signatory is the name on the attached signature and
  // the place has its own setting. Without, the backend falls back to the
  // first line of the email signature and the city in the org's address.
  const signatoryPath = flags.ENABLE_ORG_SIGNATURES
    ? SETTINGS_PATHS.signatures
    : inquirySettings
      ? SETTINGS_PATHS.emailSending
      : null
  const placePath = flags.ENABLE_ORG_SIGNATURES
    ? SETTINGS_PATHS.conformityDeclarations
    : SETTINGS_PATHS.general
  const bodies: Record<string, string> = {
    SCIP_REFERENCE_MISSING: translate("MC_CONF_V2_ERR_SCIP", {
      n:
        errors.find((e) => e.code === "SCIP_REFERENCE_MISSING")?.products
          ?.length ?? 0,
    }),
    SIGNATORY_MISSING: translate("MC_CONF_V2_ERR_SIGNATORY"),
    PLACE_MISSING: translate("MC_CONF_V2_ERR_PLACE"),
    NO_FRAMEWORK: translate("MC_CONF_V2_ERR_NO_FRAMEWORK"),
    STALE_EVALUATION: translate("MC_CONF_V2_ERR_STALE"),
    // General scope only (TUR-1573): no single "fix" button, since the three
    // resolutions live as per-row actions in the warning panel above.
    CONTRADICTING_DATA: translate("MC_CONF_V2_ERR_CONTRADICTING", {
      n:
        errors.find((e) => e.code === "CONTRADICTING_DATA")?.frameworks
          ?.length ?? 0,
    }),
    DOCUMENT_TOO_LARGE: translate("MC_CONF_V2_ERR_DOCUMENT_TOO_LARGE"),
  }
  const labels: Record<string, string> = {
    SCIP_REFERENCE_MISSING: translate("MC_CONF_V2_LBL_SCIP"),
    SIGNATORY_MISSING: translate("MC_CONF_V2_LBL_SIGNATORY"),
    PLACE_MISSING: translate("MC_CONF_V2_LBL_PLACE"),
    NO_FRAMEWORK: translate("MC_CONF_V2_LBL_NO_FRAMEWORK"),
    STALE_EVALUATION: translate("MC_CONF_V2_LBL_STALE"),
    DOCUMENT_TOO_LARGE: translate("MC_CONF_V2_LBL_DOCUMENT_TOO_LARGE"),
    CONTRADICTING_DATA: translate("MC_CONF_V2_LBL_CONTRADICTING"),
  }
  const fixes: Record<string, { label: string; run: () => void } | undefined> =
    {
      SCIP_REFERENCE_MISSING: onScipOverride
        ? {
            label: translate("MC_CONF_V2_FIX_SCIP_OVERRIDE"),
            run: () =>
              onScipOverride(
                errors.find((e) => e.code === "SCIP_REFERENCE_MISSING")
                  ?.products ?? [],
              ),
          }
        : undefined,
      // Pick a signature right here if there is one, else make one.
      SIGNATORY_MISSING: onChooseSignature
        ? {
            label: translate("MC_CONF_V2_FIX_CHOOSE_SIGNATURE"),
            run: onChooseSignature,
          }
        : signatoryPath
          ? {
              label: translate("MC_CONF_V2_FIX_OPEN_SETTINGS"),
              run: () => navigate(signatoryPath),
            }
          : undefined,
      PLACE_MISSING: {
        label: translate("MC_CONF_V2_FIX_OPEN_SETTINGS"),
        run: () => navigate(placePath),
      },
      NO_FRAMEWORK: {
        label: translate("MC_CONF_V2_FIX_SELECT_FRAMEWORK"),
        run: onDismiss,
      },
      STALE_EVALUATION: {
        label: translate("MC_CONF_V2_EDIT_REEVALUATE"),
        run: onReEvaluate,
      },
    }
  return (
    <div
      ref={panelRef}
      data-testid="refused-panel"
      className="shrink-0 overflow-hidden rounded-xl border border-destructive/60"
    >
      <div className="flex items-center gap-2 border-b border-white/[0.07] px-3.5 py-2.5 text-[12.5px] font-semibold text-destructive">
        <AlertCircle size={14} />
        {translate("MC_CONF_V2_REFUSED_TITLE")}
        <span className="flex-1" />
        <button
          type="button"
          onClick={onDismiss}
          className="border-0 bg-transparent p-0 text-[11.5px] font-normal text-grey5"
        >
          {translate("MC_CONF_V2_REFUSED_DISMISS")}
        </button>
      </div>
      {errors.map((error) => {
        const fix = fixes[error.code]
        return (
          <div
            key={error.code}
            className="flex items-start gap-3 border-b border-white/[0.07] px-3.5 py-2.5 last:border-b-0"
          >
            {/* A translated label, not the wire enum: "SIGNATORY MISSING" is our
                vocabulary, not the user's. An unknown code still falls back to the
                readable form rather than rendering nothing. */}
            <span className="w-[150px] flex-none pt-0.5 text-[10.5px] tracking-[0.04em] text-grey5 uppercase">
              {labels[error.code] ?? error.code.replaceAll("_", " ")}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] text-white">
                {bodies[error.code] ?? error.code}
              </div>
              {error.products && error.products.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {error.products.slice(0, REFUSAL_CHIP_CAP).map((p) =>
                    canEditProducts ? (
                      <button
                        key={p.productId}
                        type="button"
                        onClick={() =>
                          onOpenScip({
                            productId: p.productId,
                            productNumber: p.productNumber,
                          })
                        }
                        className="inline-flex items-center gap-1.5 rounded-md border border-grey4 bg-transparent px-2 py-1 text-[11.5px] text-white"
                      >
                        <span className="font-mono">{p.productNumber}</span>
                        <ArrowUpRight size={11} />
                      </button>
                    ) : (
                      <span
                        key={p.productId}
                        title={translate("MC_CONF_V2_EDIT_SCIP_NO_PERMISSION")}
                        className="font-mono inline-flex items-center rounded-md border border-grey4 px-2 py-1 text-[11.5px] text-grey5"
                      >
                        {p.productNumber}
                      </span>
                    ),
                  )}
                  {error.products.length > REFUSAL_CHIP_CAP && (
                    <OverflowChip
                      count={error.products.length - REFUSAL_CHIP_CAP}
                      testId="refused-products-more"
                      interactive
                    >
                      {error.products.slice(REFUSAL_CHIP_CAP).map((p) =>
                        canEditProducts ? (
                          <button
                            key={p.productId}
                            type="button"
                            onClick={() =>
                              onOpenScip({
                                productId: p.productId,
                                productNumber: p.productNumber,
                              })
                            }
                            className="font-mono inline-flex items-center gap-1.5 rounded-md border border-grey4 bg-transparent px-2 py-1 text-[11.5px] text-white"
                          >
                            {p.productNumber}
                            <ArrowUpRight size={11} />
                          </button>
                        ) : (
                          <span
                            key={p.productId}
                            className="font-mono inline-flex items-center rounded-md border border-grey4 px-2 py-1 text-[11.5px] text-grey5"
                          >
                            {p.productNumber}
                          </span>
                        ),
                      )}
                    </OverflowChip>
                  )}
                </div>
              )}
            </div>
            {fix && (
              <button
                type="button"
                onClick={fix.run}
                className="flex-none rounded-md border border-grey4 px-2.5 py-1.5 text-[11.5px] font-medium text-white hover:border-grey5"
              >
                {fix.label}
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** Split-into-per-product confirmation (review F12): deselections and
 *  overrides on the combined draft don't carry over to the fresh per-product
 *  evaluations, so this warns before the (irreversible from here) split —
 *  same Dialog primitives and pending/confirm shape as OverrideDialog. */
function SplitConfirmDialog({
  pending,
  onConfirm,
  onClose,
}: {
  pending: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  const { translate } = useTranslation()
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {translate("MC_CONF_V2_SPLIT_CONFIRM_TITLE")}
          </DialogTitle>
          <p className="text-[12.5px] leading-relaxed text-grey5">
            {translate("MC_CONF_V2_SPLIT_CONFIRM_BODY")}
          </p>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            className="h-9 text-[13px]"
            onClick={onClose}
          >
            {translate("MC_CS_CONF_EDIT_CANCEL")}
          </Button>
          <Button
            className="h-9 bg-yellow1 text-[13px] font-semibold text-black hover:bg-yellow2 disabled:opacity-40"
            disabled={pending}
            onClick={onConfirm}
          >
            {pending && <Loader2 size={13} className="mr-1.5 animate-spin" />}
            {translate("MC_CONF_V2_SPLIT_CONFIRM_ACTION")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Inline own-SCIP capture (Q16/Q20) — saves on the product, then the caller
 *  re-evaluates so the gate clears without leaving the editor. */
function ScipDialog({
  productNumber,
  pending,
  onSave,
  onClose,
  overrideWarning,
}: {
  productNumber: string | null
  pending: boolean
  onSave: (scipNumber: string) => void
  onClose: () => void
  overrideWarning: string | null
}) {
  const { translate } = useTranslation()
  const [value, setValue] = useState("")
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {translate("MC_CONF_V2_EDIT_TH_OWN_SCIP")}
            {productNumber && (
              <span className="font-mono ml-2 text-[13px] text-yellow1">
                {productNumber}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={translate("MC_CONF_V2_SCIP_PLACEHOLDER")}
          aria-label={translate("MC_CONF_V2_EDIT_TH_OWN_SCIP")}
          className="font-mono w-full rounded-md border border-grey4 bg-grey3 px-3 py-2 text-[12.5px] text-white outline-hidden focus:border-yellow1"
        />
        {overrideWarning && (
          <p className="flex items-start gap-1.5 text-[11.5px] text-warning">
            <AlertCircle size={13} className="mt-0.5 shrink-0" />
            {overrideWarning}
          </p>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            className="h-9 text-[13px]"
            onClick={onClose}
          >
            {translate("MC_CS_CONF_EDIT_CANCEL")}
          </Button>
          <Button
            className="h-9 bg-yellow1 text-[13px] font-semibold text-black hover:bg-yellow2 disabled:opacity-40"
            disabled={value.trim().length === 0 || pending}
            onClick={() => onSave(value.trim())}
          >
            {pending && <Loader2 size={13} className="mr-1.5 animate-spin" />}
            {translate("MC_CONF_V2_EDIT_ADD_ON_PRODUCT")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Continue without the issuer's own SCIP number (tester feedback item 2):
 *  same reason rule and audit hint as OverrideDialog. The caller owns the
 *  mutation. */
function ScipOverrideDialog({
  products,
  pending,
  onConfirm,
  onClose,
}: {
  products: ConformityV2ApproveErrorProduct[]
  pending: boolean
  onConfirm: (reason: string) => void
  onClose: () => void
}) {
  const { translate } = useTranslation()
  const [reason, setReason] = useState("")
  const valid = reason.trim().length >= MIN_REASON

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{translate("MC_CONF_V2_FIX_SCIP_OVERRIDE")}</DialogTitle>
          <p className="text-[12.5px] leading-relaxed text-grey5">
            {translate("MC_CONF_V2_SCIP_OVERRIDE_BODY")}
          </p>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {products.slice(0, REFUSAL_CHIP_CAP).map((p) => (
            <span
              key={p.productId}
              className="font-mono inline-flex items-center rounded-md border border-grey4 px-2 py-1 text-[11.5px] text-white"
            >
              {p.productNumber}
            </span>
          ))}
          {products.length > REFUSAL_CHIP_CAP && (
            <OverflowChip count={products.length - REFUSAL_CHIP_CAP}>
              {products.slice(REFUSAL_CHIP_CAP).map((p) => (
                <span
                  key={p.productId}
                  className="font-mono inline-flex items-center rounded-md border border-grey4 px-2 py-1 text-[11.5px] text-grey5"
                >
                  {p.productNumber}
                </span>
              ))}
            </OverflowChip>
          )}
        </div>
        <div>
          <label
            htmlFor="scip-override-reason"
            className="mb-1.5 block text-[10.5px] font-semibold tracking-widest text-grey5 uppercase"
          >
            {translate("MC_CONF_V2_OVERRIDE_REASON")}
          </label>
          <textarea
            id="scip-override-reason"
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={translate("MC_CONF_V2_SCIP_OVERRIDE_PLACEHOLDER")}
            className="w-full resize-y rounded-md border border-grey4 bg-grey3 px-3 py-2 text-[12.5px] text-white outline-hidden focus:border-yellow1"
          />
          <div
            className={cn(
              "mt-1 flex items-center justify-between text-[11px]",
              valid ? "text-grey5" : "text-warning",
            )}
          >
            <span>
              {valid
                ? translate("MC_CONF_V2_OVERRIDE_LOGGED")
                : translate("MC_CONF_V2_OVERRIDE_MIN")}
            </span>
            <span className="font-mono">
              {reason.trim().length} / {MIN_REASON}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            className="h-9 text-[13px]"
            onClick={onClose}
          >
            {translate("MC_CS_CONF_EDIT_CANCEL")}
          </Button>
          <Button
            className="h-9 bg-yellow1 text-[13px] font-semibold text-black hover:bg-yellow2 disabled:opacity-40"
            disabled={!valid || pending}
            onClick={() => onConfirm(reason.trim())}
          >
            {pending && <Loader2 size={13} className="mr-1.5 animate-spin" />}
            {translate("MC_CONF_V2_SCIP_OVERRIDE_CONFIRM")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The issued artefact itself (review NB1): the frozen PDF bytes in the
 *  app's unified `DeclarationDocumentViewer` (the same viewer Supplier
 *  Studio v2's `DocumentPane` uses) — never a live re-render that can drift
 *  from what the customer received, and never a raw iframe. Read-only here:
 *  no evidence sources, no citation highlight — this is a plain read of the
 *  frozen file, so `highlight`/`sources`/`activeStatementId` stay empty. */
function FrozenPdfPane({
  declarationId,
  translate,
  scale,
  onCurrentPage,
}: {
  declarationId: string
  translate: Translate
  scale?: PdfPaneZoom
  onCurrentPage: (current: number, total: number) => void
}) {
  const pdf = useConformityFrozenPdfUrl(declarationId)
  const [barOpen, setBarOpen] = useState(false)
  const [tokens, setTokens] = useState<FilterToken[]>([])
  const [search, setSearch] = useState("")
  if (pdf.isLoading) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center gap-2 text-[12px] text-grey5">
        <Loader2 size={14} className="animate-spin" />
        {translate("MC_CS_CONF_EDIT_LOADING")}
      </div>
    )
  }
  if (!pdf.data) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center text-[12px] text-grey5">
        {translate("MC_CS_CONF_EDIT_LOAD_ERROR")}
      </div>
    )
  }
  return (
    <DeclarationDocumentViewer
      className="min-h-0 flex-1"
      fileUrl={pdf.data}
      highlight={null}
      sources={[]}
      activeStatementId={null}
      open={barOpen}
      onOpenChange={setBarOpen}
      tokens={tokens}
      search={search}
      onTokensChange={setTokens}
      onSearchChange={setSearch}
      placeholder={translate("MC_DECL_V2_DOC_FIND_PLACEHOLDER")}
      scale={scale}
      onCurrentPage={onCurrentPage}
    />
  )
}

/** Design's "Nothing to issue yet" page (review NF5a). No document exists
 *  yet, so it sits on the dark pane itself rather than imitating a paper page.
 *  `blocked`: at least one framework is kept out by missing supplier data or
 *  an adverse finding. Without it the pane is empty only because nothing is
 *  ticked yet, so the copy asks for a selection and the data steps stay out. */
function EmptyDocState({
  translate,
  blocked,
}: {
  translate: Translate
  blocked: boolean
}) {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto p-6">
      <div className="flex w-full max-w-[380px] flex-col items-center text-center">
        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-white/[0.07] bg-white/3">
          <FileClock size={18} className="text-grey5" aria-hidden="true" />
        </div>
        <div className="text-[15px] font-semibold text-white">
          {translate("MC_CONF_V2_EMPTY_TITLE")}
        </div>
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-grey5">
          {translate(
            blocked
              ? "MC_CONF_V2_EMPTY_BODY"
              : "MC_CONF_V2_EMPTY_BODY_UNSELECTED",
          )}
        </p>
        {blocked && (
          <ol className="mt-6 w-full divide-y divide-white/[0.07] overflow-hidden rounded-lg border border-white/[0.07] bg-white/2 text-left">
            {(
              [
                "MC_CONF_V2_EMPTY_STEP1",
                "MC_CONF_V2_EMPTY_STEP2",
                "MC_CONF_V2_EMPTY_STEP3",
              ] as const
            ).map((key, i) => (
              <li
                key={key}
                className="flex items-start gap-3 px-3.5 py-2.5 text-[12.5px] leading-snug text-white"
              >
                <span className="mt-px flex h-5 w-5 flex-none items-center justify-center rounded-full bg-white/6 text-[10.5px] font-semibold text-grey5 tabular-nums">
                  {i + 1}
                </span>
                {translate(key)}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  )
}

/** Shimmer rows while a re-evaluation runs (review NF5c). */
function EvaluatingSkeleton({
  count,
  translate,
}: {
  count: number
  translate: Translate
}) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2 text-[12px] text-grey5">
        <span className="h-2 w-2 animate-pulse rounded-full bg-yellow1" />
        {translate("MC_CONF_V2_EDIT_EVALUATING_HINT", { n: count })}
      </div>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex animate-pulse items-center gap-3 rounded-xl border border-white/[0.07] p-3.5"
        >
          <div className="h-4 w-4 rounded bg-grey4" />
          <div className="flex flex-1 flex-col gap-1.5">
            <div className="h-3 w-2/5 rounded bg-grey4" />
            <div className="h-2 w-3/5 rounded bg-grey4/60" />
          </div>
          <div className="h-5 w-20 rounded-full bg-grey4/60" />
        </div>
      ))}
    </div>
  )
}

/** The Email modal: the ONE place the cover email is written. Subject, body
 *  and the cc chips are editable here; the frozen PDF rides along as the
 *  attachment. Overrides are persisted by the send endpoint, so there is no
 *  separate save step. */
function SendDialogV2({
  detail,
  onClose,
}: {
  detail: ConformityDetail
  onClose: () => void
}) {
  const { translate } = useTranslation()
  const send = useSendConformity()
  const sendTest = useSendConformityTest()
  const [subject, setSubject] = useState(
    detail.coverSubject ?? buildDefaultSubject(detail),
  )
  const [body, setBody] = useState(detail.coverBody ?? buildDefaultBody(detail))
  // The customer contact can be dropped from THIS send (X on the chip); the
  // contact itself stays in master data. Without it the extra recipients
  // become the TO line so the mail still has an addressee.
  const [primaryRemoved, setPrimaryRemoved] = useState(false)
  // The chip editor persists on its own (useUpdateConformityRecipients); the
  // detail query then refetches. Without this, Send could fire on the stale
  // recipient list mid-save (review F10).
  const [recipientsPending, setRecipientsPending] = useState(false)
  const primary = primaryRemoved ? null : detail.recipientEmail
  const extras = detail.extraRecipientEmails ?? []
  const to = primary ? [primary] : extras
  const cc = primary ? extras : []

  // Sending is two clicks, not one: the dialog prefills the customer's real
  // address from master data, and the tester opened it, saw M-TEC's address and
  // cancelled rather than risk it (F23). The first click lists who is about to
  // receive this and where each address came from; only the second sends.
  const [confirming, setConfirming] = useState(false)

  async function onSend() {
    if (to.length === 0 || send.isPending) return
    if (!confirming) {
      setConfirming(true)
      return
    }
    try {
      await send.mutateAsync({
        id: detail.id,
        to,
        cc,
        coverSubject: subject,
        coverBody: body,
      })
      onClose()
      toast.success(translate("MC_CS_CONF_EDIT_TOAST_SENT"))
    } catch {
      toast.error(translate("MC_CS_CONF_EDIT_TOAST_SEND_ERR"))
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send size={14} className="text-yellow1" />
            {translate("MC_CONF_V2_SEND_TITLE")}
          </DialogTitle>
        </DialogHeader>

        <div>
          <SendLabel>{translate("MC_CONF_V2_SEND_TO")}</SendLabel>
          <ConformityRecipientEditor
            declarationId={detail.id}
            primary={primary}
            extras={extras}
            locked={false}
            onRemovePrimary={
              detail.recipientEmail ? () => setPrimaryRemoved(true) : undefined
            }
            onPendingChange={setRecipientsPending}
          />
        </div>

        <div>
          <SendLabel>{translate("MC_CONF_V2_SEND_SUBJECT")}</SendLabel>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            aria-label={translate("MC_CONF_V2_SEND_SUBJECT")}
            className="w-full rounded-md border border-grey4 bg-grey3 px-3 py-2 text-[12.5px] text-white outline-hidden focus:border-yellow1"
          />
        </div>

        <div>
          <SendLabel>{translate("MC_CONF_V2_SEND_COVER")}</SendLabel>
          <textarea
            rows={9}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            aria-label={translate("MC_CONF_V2_SEND_COVER")}
            className="w-full resize-y rounded-md border border-grey4 bg-grey3 px-3 py-2 text-[12.5px] leading-relaxed text-white outline-hidden focus:border-yellow1"
          />
        </div>

        <p className="inline-flex items-center gap-1.5 text-[11px] text-grey5">
          <FileText size={11} />
          {translate("MC_CONF_V2_SEND_ATTACHMENT", {
            file: `${detail.reference ?? detail.id}.pdf`,
          })}
        </p>

        {confirming && (
          <div className="rounded-md border border-yellow1/40 bg-yellow1/6 p-3">
            <p className="text-[12px] font-semibold text-white">
              {translate("MC_CONF_V2_SEND_CONFIRM_TITLE")}
            </p>
            <ul className="mt-2 flex flex-col gap-1">
              {[
                ...to.map((email) => ({ email, kind: "to" as const })),
                ...cc.map((email) => ({ email, kind: "cc" as const })),
              ].map(({ email, kind }) => (
                <li
                  key={`${kind}-${email}`}
                  className="flex items-baseline justify-between gap-3 text-[12px] text-white"
                >
                  <span>
                    <span className="mr-1.5 text-grey5">
                      {translate(
                        kind === "to"
                          ? "MC_CONF_V2_SEND_LBL_TO"
                          : "MC_CONF_V2_SEND_LBL_CC",
                      )}
                    </span>
                    {email}
                  </span>
                  <span className="shrink-0 text-[11px] text-grey5">
                    {translate(
                      email === detail.recipientEmail
                        ? "MC_CONF_V2_SEND_SRC_PRIMARY"
                        : "MC_CONF_V2_SEND_SRC_ADDED",
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            className="h-9 text-[13px]"
            disabled={send.isPending}
            onClick={onClose}
          >
            {translate("MC_CS_CONF_EDIT_CANCEL")}
          </Button>
          {/* Deliberately passes no recipients: the server addresses the caller
              from the token. Lets a pilot user see what the customer would get
              without the customer getting it (F23). */}
          <Button
            variant="outline"
            className="h-9 text-[13px]"
            disabled={send.isPending || sendTest.isPending}
            onClick={async () => {
              try {
                const { sentTo } = await sendTest.mutateAsync({
                  id: detail.id,
                  coverSubject: subject,
                  coverBody: body,
                })
                toast.success(
                  translate("MC_CONF_V2_SEND_TEST_OK", { email: sentTo }),
                )
              } catch {
                toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
              }
            }}
          >
            {sendTest.isPending ? (
              <Loader2 size={13} className="mr-1.5 animate-spin" />
            ) : null}
            {translate("MC_CONF_V2_SEND_TEST")}
          </Button>
          <Button
            className="h-9 bg-yellow1 text-[13px] font-semibold text-black hover:bg-yellow2 disabled:opacity-40"
            disabled={to.length === 0 || recipientsPending || send.isPending}
            onClick={onSend}
          >
            {send.isPending ? (
              <Loader2 size={13} className="mr-1.5 animate-spin" />
            ) : (
              <Send size={13} className="mr-1.5" />
            )}
            {confirming
              ? translate("MC_CONF_V2_SEND_CONFIRM_BTN", {
                  n: String(to.length + cc.length),
                })
              : translate("MC_CONF_V2_SEND_NOW")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SendLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1.5 text-[10.5px] font-semibold tracking-widest text-grey5 uppercase">
      {children}
    </div>
  )
}
